import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Subject, map, takeUntil } from 'rxjs';
import { TranslateService } from 'src/app/modules/core/services';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { FeedbackTextComponent } from '../../modules/shared/components/feedback-text/feedback-text.component';
import { RegisterDamageRiskComponent } from '../register-damage-risk/register-damage-risk.component';
import { HistoryComponent } from '../history/history.component';
import { WarningSelectionOverviewComponent } from './components/warning-selection-overview/warning-selection-overview.component';
import { LineSelectionOverviewComponent } from './components/line-selection-overview/line-selection-overview.component';
import { PunctualitySelectionOverviewComponent } from './components/punctuality-selection-overview/punctuality-selection-overview.component';
import { VehicleSelectionOverviewComponent } from './components/vehicle-selection-overview/vehicle-selection-overview.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { AsyncPipe } from '@angular/common';
import { TrafficDeviationsSelectOverviewComponent } from './components/traffic-deviations-select-overview/traffic-deviations-select-overview.component';
import { CreateTrafficDeviationOverviewComponent } from './components/create-traffic-deviation-overview/create-traffic-deviation-overview.component';
import { EditTrafficDeviationTemplatesOverviewComponent } from './components/edit-traffic-deviation-templates-overview/edit-traffic-deviation-templates-overview.component';
import { SettingsOverviewComponent } from './components/settings-overview/settings-overview.component';
import { HomeViewComponent } from '../home-view/home-view.component';
import { SELECTION_OVERVIEW_WINDOW_LARGE_WIDTH, SELECTION_OVERVIEW_WINDOW_SMALL_WIDTH } from '@modules/core/models/app-state';
import { Garage } from '@modules/shared/models/garage';

@Component({
    selector: 'app-selection-overview',
    templateUrl: './selection-overview.component.html',
    styleUrls: ['./selection-overview.component.scss'],
    imports: [
        BladeHeaderComponent,
        TranslateModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        HomeViewComponent,
        VehicleSelectionOverviewComponent,
        PunctualitySelectionOverviewComponent,
        LineSelectionOverviewComponent,
        WarningSelectionOverviewComponent,
        HistoryComponent,
        RegisterDamageRiskComponent,
        FeedbackTextComponent,
        AsyncPipe,
        CreateTrafficDeviationOverviewComponent,
        TrafficDeviationsSelectOverviewComponent,
        EditTrafficDeviationTemplatesOverviewComponent,
        SettingsOverviewComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class SelectionOverviewComponent implements OnInit, OnDestroy {
    private readonly _appState = inject(AppStateService);
    private readonly _translate = inject(TranslateService);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);

    public windowWidthSmall = SELECTION_OVERVIEW_WINDOW_SMALL_WIDTH;
    public widthsByContext = signal<{ [key: string]: string }>({});
    public nrItems = signal<number | undefined>(undefined);
    public isNoGarageSelected = signal<boolean>(false);
    public currentContext$ = this._appState.getPropertyState('selectedContext');
    public mappedWidthsByContext$ = this._appState.getPropertyState(
        'selectionOverviewWidthByContext').pipe(
            map((width: {[key: string]: string}) => {
                return Object.fromEntries(Object.entries(width).map(([key, value]) => {
                    return [key, value.includes('px') ? +value.replace('px', '') : 0]
                }))
            }));

    private readonly _ngUnsubscribe$ = new Subject<void>();

    ngOnInit(): void {        
        this._appState.getPropertyState('selectionOverviewWidthByContext').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((widths: { [key: string]: string }) => {
            this.widthsByContext.set(widths);
            this._changeDetectorRef.detectChanges();
        });

        this._appState.getPropertyState('selectedGarageCodes').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((selectedGarageCodes: Garage[]) => {
            this.isNoGarageSelected.set(selectedGarageCodes?.length === 0);
        });

        this._appState.getPropertyState('selectedContext').pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(() => {
            this.nrItems.set(undefined);
            this._changeDetectorRef.detectChanges();
        });
    }

    public ngOnDestroy(): void {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    public closePanel(): void {
        this._appState.closeSelectionPanel();
    }

    public toggleWideWidth(contextKey: string): void {
        const currentWidth = this.widthsByContext()[contextKey];
        const newWidth = +currentWidth.replace('px', '') > SELECTION_OVERVIEW_WINDOW_SMALL_WIDTH 
            ? SELECTION_OVERVIEW_WINDOW_SMALL_WIDTH 
            : SELECTION_OVERVIEW_WINDOW_LARGE_WIDTH;
        this._appState.setWidthForAContext(contextKey, newWidth);
    }

    public updateNrItems(count: number): void {
        // Defer signal update to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => this.nrItems.set(count), 0);
    }

    public get shrinkTooltip(): void {
        return this._translate.instant('Minify');
    }
    public get expandTooltip(): void {
        return this._translate.instant('Expand');
    }
}